import { CommonModule, DecimalPipe } from "@angular/common";
import { Component, ElementRef, Input, OnInit } from "@angular/core";
import { ActivationEnd, Router } from "@angular/router";
import { AntiAlias } from "aws-sdk/clients/mediaconvert";
import { truncateSync } from "fs";
import {
  CarouselModule,
  OwlOptions,
  SlidesOutputData,
} from "ngx-owl-carousel-o";
import { Subscription, VirtualTimeScheduler } from "rxjs";
import {
  ClinicStoplight,
  DataStream,
  KpiCartesian,
  LabKpiModel,
} from "../shared/models/kpi.model";
import { RouteEnum } from "../shared/models/routes.model";
import { AuthService } from "../shared/services/auth.service";
import { ReportsService } from "../shared/services/reports.service";
import { BroshService } from "../shared/services/brosh.service";
import { Store } from "@ngrx/store";
import { GO_TO_CLINIC_KPI } from "../shared/ngrx/ngrx-actions";
import { GenKpiCartesianComponent } from "./gen-kpi-cartesian/gen-kpi-cartesian.component";
import { LoaderComponent } from "../shared/components/ui/loader/loader.component";
import { GenKpiDoughnutComponent } from "./gen-kpi-doughnut/gen-kpi-doughnut.component";
import { GenKpiCircleComponent } from "./gen-kpi-circle/gen-kpi-circle.component";
import { GenKpiBarComponent } from "./gen-kpi-bar/gen-kpi-bar.component";
import { GenKpiRadarComponent } from "./gen-kpi-radar/gen-kpi-radar.component";
import { SlideInOutComponent } from "../shared/components/ui/slide-in-out.component";
import { ModalPopupComponent } from "../shared/components/ui/modal-popup.component";
import { FormsModule } from "@angular/forms";
import { InputWrapComponent } from "../shared/components/ui/input-wrap.component";
import { DatePickerModule } from "primeng/datepicker";
import { SelectModule } from "primeng/select";
import { kpiConfig } from "../shared/ngrx/ngrx-selectors";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "lab-kpi",
  templateUrl: "./lab-kpi.component.html",
  styleUrls: ["./lab-kpi.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    GenKpiCartesianComponent,
    CarouselModule,
    LoaderComponent,
    GenKpiDoughnutComponent,
    GenKpiCircleComponent,
    GenKpiBarComponent,
    GenKpiRadarComponent,
    SlideInOutComponent,
    ModalPopupComponent,
    FormsModule,
    InputWrapComponent,
    DatePickerModule,
    SelectModule,
  ],
})
export class LabKpiComponent {
  @Input() tag: string;
  @Input() filter: string;
  @Input() filterType: string;
  routeEnum: RouteEnum = new RouteEnum();

  customOptions: OwlOptions = {
    loop: false,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: false,
    nav: false,
    autoWidth: true,
    rtl: true,
    margin: 10,
    autoHeight: false,
  };

  loading: boolean = true;
  todayOrdersDonut: DataStream;
  invoicesDonut: DataStream;
  onlineActivityDonut: DataStream;
  cumulMonthIncome: DataStream[] = [];
  cartesianTabSelected?: string;
  cartesianTabsAvailable: string[] = [];
  periodicProfit: DataStream[];
  cycleDuration: DataStream[];
  digitalOrders: DataStream[];
  plantOrders: DataStream[];
  supplyOrders: DataStream[];
  navBtnPrevEnabled: boolean = true;
  navBtnNextEnabled: boolean = true;
  refreshTimer: any;
  refreshIntervalSeconds: number = 60 * 10;
  routerSub: Subscription;
  missedRefreshWhileDetached: boolean = false;
  displayClinicList: boolean;

  clinics: ClinicStoplight[] = [];
  clinicListTitle: string;
  clinicsStoplightLoading: boolean;
  itemsRadar: DataStream[];
  tryInTrends: DataStream[];
  fixTrends: DataStream[];
  returnTrends: DataStream[];
  kpi: LabKpiModel;
  abandonClinicsSw: boolean;
  failedAcuisitionSw: boolean;
  toggleSnoozeInput: boolean;
  snoozeNote: string;
  snoozeEndDate: Date;
  selectedEmployeePerformance: KpiCartesian;
  employeePerformanceOptions: KpiCartesian[] = [];

  monthNames: [
    "ינואר",
    "פברואר",
    "מרץ",
    "אפריל",
    "מאי",
    "יוני",
    "יולי",
    "אוגוסט",
    "ספטמבר",
    "אוקטובר",
    "נובמבר",
    "דצמבר"
  ];

  get broshMode(): boolean {
    return this.filterType == "ALL" || this.filterType == "AGENT";
  }

  get clinicMode(): boolean {
    return this.filterType == "CLINIC";
  }

  get lastYear(): number {
    return new Date().getFullYear() - 1;
  }

  get curYear(): number {
    return new Date().getFullYear();
  }

  get isAgent(): boolean {
    return this.authService.currentUser.roleCode == "AGENT";
  }

  constructor(
    private readonly store: Store,
    private reportsService: ReportsService,
    private numberPipe: DecimalPipe,
    private router: Router,
    private authService: AuthService,
    private broshService: BroshService,
    private translateService: TranslateService,
    private elRef: ElementRef
  ) { }

  ngOnInit(): void {
    this.routerSub = this.router.events.subscribe((ev) => {
      // compared by route path: importing the host components' classes here
      // creates a circular import (kpi-brosh/kpi-clinics import LabKpiComponent
      // in their decorators), which breaks module evaluation order-dependently
      if (
        ev instanceof ActivationEnd &&
        (ev.snapshot.routeConfig?.path === "brosh" ||
          ev.snapshot.routeConfig?.path === "kpi-clinics")
      ) {
        if (this.missedRefreshWhileDetached) {
          this.missedRefreshWhileDetached = false;
          this.getData();
        } else {
          this.setupData();
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.getData();
    this.scheduleGetData();
  }

  ngOnDestroy() {
    this.clearTimer();
    this.routerSub?.unsubscribe();
  }

  scheduleGetData() {
    this.clearTimer();

    this.refreshTimer = setInterval(() => {
      // while detached by the route-reuse cache, defer the refresh —
      // the ActivationEnd handler runs getData when the screen is revisited
      if (!document.contains(this.elRef.nativeElement)) {
        this.missedRefreshWhileDetached = true;
        return;
      }
      this.getData();
    }, this.refreshIntervalSeconds * 1000);
  }

  clearTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  getData() {
    if (!this.filterType) return;

    this.loading = true;
    this.reportsService
      .GetLabKPI(this.filter, this.filterType)
      .subscribe((data) => {
        this.kpi = data;
        this.setupData();
        this.loading = false;
      });
  }

  setupData() {
    let data = this.kpi;
    if (data == null) return;

    if (data.allIncomingCases) {
      // data.digitalIncomingCases.deltaPct = 0.5;
      // data.allIncomingCases.deltaPct = 0.5;
      // data.digitalIncomingCases.cnt = 100;
      // data.allIncomingCases.cnt = 100;

      this.todayOrdersDonut = {
        streamCode: "TODAY_ORDERS",
        values: [
          data.digitalIncomingCases.deltaPct * 100,
          (data.allIncomingCases.deltaPct - data.digitalIncomingCases.deltaPct) * 100,
          100 - data.allIncomingCases.deltaPct * 100,
        ],
        txtValues: [
          data.digitalIncomingCases.cnt.toString() + " :דיגיטל",
          (data.allIncomingCases.cnt - data.digitalIncomingCases.cnt).toString() + " :אנלוג",
          100 - data.allIncomingCases.deltaPct * 100 + "%",
        ],
        bgColors: [
          this.colorCodeToRGB(data.digitalIncomingCases.circleColor),
          this.colorCodeToRGB(data.allIncomingCases.circleColor),
          "#ebeef2",
        ],
        visible: true,
      };
    }

    if (data.invoices) {
      this.invoicesDonut = {
        streamCode: "INVOICES_CLIENTS",
        values: data.invoices.valPct.map((i) => Math.round(i * 100)),
        txtValues: data.invoices.valCnt.map((i) =>
          this.numberPipe.transform(i, "1.0-0")
        ),
        labels: data.invoices.categoryDescr,
        bgColors: data.invoices.colorCode.map((i) => this.colorCodeToRGB(i)),
        visible: true,
      };
    }

    if (data.onlineUsers)
      this.onlineActivityDonut = {
        streamCode: "ONLINE_ACTIVITY",
        values: data.onlineUsers.valPct.map((i) => i * 100),
        txtValues: data.onlineUsers.valCnt.map((i) => i.toString() + " מרפאות"),
        labels: data.onlineUsers.categoryDescr,
        bgColors: data.onlineUsers.colorCode.map((i) => this.colorCodeToRGB(i)),
        visible: true,
      };

    if (data.deliveryReq) {
      let onlineIdx = data.deliveryReq.categoryCode.indexOf("ONLINE");
      let phoneIdx = data.deliveryReq.categoryCode.indexOf("PHONE");
      this.supplyOrders = [
        {
          streamCode: "LABELS",
          labels: ["הזמנות איסוף"],
          visible: true,
        },
        {
          streamCode: "ONLINE_PCT",
          color: this.colorCodeToRGB(data.deliveryReq.colorCode[onlineIdx]),
          values: [data.deliveryReq.valPct[onlineIdx] * 100],
          labels: ["הזמנות איסוף"],
          visible: true,
        },
        {
          streamCode: "PHONE_PCT",
          color: this.colorCodeToRGB(data.deliveryReq.colorCode[phoneIdx]),
          values: [data.deliveryReq.valPct[phoneIdx] * 100],
          labels: ["הזמנות איסוף"],
          visible: true,
        },
        {
          streamCode: "ONLINE_CNT",
          descr: "אונליין",
          values: [data.deliveryReq.valCnt[onlineIdx]],
          visible: false,
        },
        {
          streamCode: "PHONE_CNT",
          descr: "טלפוניות",
          values: [data.deliveryReq.valCnt[phoneIdx]],
          visible: false,
        },
      ];
    }

    if (data.plantOrderStatus)
      this.plantOrders = [
        {
          streamCode: "LABELS",
          labels: data.plantOrderStatus.plantName,
        },
        {
          streamCode: "REGULAR",
          descr: "regular",
          color: "rgb(50, 195, 125)",
          labels: data.plantOrderStatus.plantName,
          values: data.plantOrderStatus.regularCnt,
          visible: true,
        },
        {
          streamCode: "CRITICAL",
          descr: "critical",
          color: "rgb(255, 187, 0)",
          labels: data.plantOrderStatus.plantName,
          values: data.plantOrderStatus.criticalCnt,
          visible: true,
        },
        {
          streamCode: "OVERDUE",
          descr: "overdue",
          color: "rgb(255, 105, 105)",
          labels: data.plantOrderStatus.plantName,
          values: data.plantOrderStatus.overdueCnt,
          visible: true,
        },
        {
          streamCode: "REGULAR_PCT",
          labels: data.plantOrderStatus.plantName,
          values: data.plantOrderStatus.regularPct,
          visible: false,
        },
        {
          streamCode: "CRITICAL_PCT",
          labels: data.plantOrderStatus.plantName,
          values: data.plantOrderStatus.criticalPct,
          visible: false,
        },
        {
          streamCode: "OVERDUE_PCT",
          labels: data.plantOrderStatus.plantName,
          values: data.plantOrderStatus.overduePct,
          visible: false,
        },
        {
          streamCode: "TOTAL",
          labels: data.plantOrderStatus.plantName,
          values: data.plantOrderStatus.totCnt,
          visible: false,
        },
      ];

    if (data.cycleDuration) {
      // Backend sends two period datastreams (open cycles / cycles completed
      // in the window), each a donut-shaped breakdown for one bar. Transpose to
      // series-major datasets (one per category) so each stacked segment keeps
      // its color across both bars.
      let cur = data.cycleDuration.current;
      let completed = data.cycleDuration.completed;
      let periods = [];
      let barLabels = [];
      if (completed) {
        periods.push(completed);
        barLabels.push(
          this.translateService.instant("translate.kpi.cycleDuration.completed")
        );
      }
      if (cur) {
        periods.push(cur);
        barLabels.push(
          this.translateService.instant("translate.kpi.cycleDuration.current")
        );
      }
      let template = periods[0];
      if (template) {
        this.cycleDuration = [{ streamCode: "LABELS", labels: barLabels }];
        template.categoryDescr.forEach((descr, i) => {
          this.cycleDuration.push({
            streamCode: template.categoryCode ? template.categoryCode[i] : "CAT_" + i,
            descr: descr,
            color: this.colorCodeToRGB(template.colorCode[i]),
            labels: barLabels,
            values: periods.map((p) => p.valPct[i]),
            txtValues: periods.map((p) => p.valCnt[i]),
            visible: true,
          });
        });
      }
    }

    if (data.cumulIncome) {
      this.cumulMonthIncome = [
        {
          streamCode: "PAST_MONTH",
          descr: "בחודש הקודם",
          color: "rgb(255, 187, 0)",
          labels: data.cumulIncome.past.dates,
          values: data.cumulIncome.past.valCnt,
          unitDescr: data.cumulIncome.past.unitDescr,
          visible: true,
        },
        {
          streamCode: "PAST_MONTH2",
          descr: "לפני חודשיים",
          color: this.colorCodeToRGB("GREEN"),
          labels: data.cumulIncome.past2.dates,
          values: data.cumulIncome.past2.valCnt,
          unitDescr: data.cumulIncome.past2.unitDescr,
          visible: true,
        },
        {
          streamCode: "CUR_MONTH",
          descr: "בחודש הנוכחי",
          color: "rgb(0, 148, 221)",
          labels: data.cumulIncome.current.dates,
          values: data.cumulIncome.current.valCnt,
          unitDescr: data.cumulIncome.current.unitDescr,
          visible: true,
        },
      ];

      if (data.cumulIncome.breakeven) {
        this.cumulMonthIncome.push({
          streamCode: "BREAK_EVEN",
          descr: "Break even",
          color: "black",
          labels: data.cumulIncome.breakeven.dates,
          values: data.cumulIncome.breakeven.valCnt,
          visible: true,
          dashed: true,
        });
      }
    }

    if (data.profitTrends) {
      this.periodicProfit = [
        {
          streamCode: "LABELS",
          labels: this.monthNames,
        },

        {
          streamCode: "CUR_INCOME",
          descr: this.curYear + " הכנסות",
          color: this.colorCodeToRGB("BLUE"),
          fillColor: this.colorCodeToRGB("BLUE"),
          labels: data.profitTrends.income.monthName,
          values: data.profitTrends.income.valCnt,
          visible: true,
          fill: false,
        },
        {
          streamCode: "PAST_INCOME",
          descr: this.lastYear + " הכנסות",
          color: this.colorCodeToRGB("YELLOW"),
          labels: data.profitTrends.incomePrev.monthName,
          values: data.profitTrends.incomePrev.valCnt,
          visible: true,
          hidden: true,
        },
        {
          streamCode: "CUR_ORDER_PLACEMENT",
          descr: this.curYear + " הזמנות נכנסות",
          color: this.colorCodeToRGB("PURPLE"),
          labels: data.profitTrends.orderPlacement.monthName,
          values: data.profitTrends.orderPlacement.valCnt,
          visible: true,
        },
      ];

      if (data.profitTrends.profit) {
        this.periodicProfit.push(
          ...[
            {
              streamCode: "CUR_PROFIT",
              descr: this.curYear + " רווחיות",
              color: this.colorCodeToRGB("BLUE"),
              fillColor: this.colorCodeToRGB("BLUE", 0.2),
              labels: data.profitTrends.profit.monthName,
              values: data.profitTrends.profit.valCnt,
              values2: data.profitTrends.profit.valSharePct,
              visible: true,
              fill: false,
              dashed: true,
            },

            {
              streamCode: "PAST_PROFIT",
              descr: this.lastYear + " רווחיות",
              color: this.colorCodeToRGB("YELLOW"),
              fillColor: this.colorCodeToRGB("YELLOW", 0.2),
              labels: data.profitTrends.profitPrev.monthName,
              values: data.profitTrends.profitPrev.valCnt,
              values2: data.profitTrends.profitPrev.valSharePct,
              visible: true,
              dashed: true,
              hidden: true,
            },
          ]
        );
      }
    }

    if (data.scanAnalog) {
      this.digitalOrders = [
        {
          streamCode: "LABELS",
          labels: data.scanAnalog.scans.monthName,
        },
        {
          streamCode: "DIGITAL_CNT",
          descr: "עבודות בסריקה",
          color: "rgb(255, 187, 0)",
          labels: data.scanAnalog.scans.monthName,
          values: data.scanAnalog.scans.valCnt,
          visible: true,
        },
        {
          streamCode: "ANALOG_CNT",
          descr: "עבודות אנלוגיות",
          color: "rgb(0, 148, 221)",
          labels: data.scanAnalog.analog.monthName,
          values: data.scanAnalog.analog.valCnt,
          visible: true,
        },
        {
          streamCode: "DIGITAL_PCT",
          labels: data.scanAnalog.scans.monthName,
          values: data.scanAnalog.scans.valSharePct,
          visible: false,
        },
        {
          streamCode: "ANALOG_PCT",
          labels: data.scanAnalog.analog.monthName,
          values: data.scanAnalog.analog.valSharePct,
          visible: false,
        },
        {
          streamCode: "DIGITAL_COLOR_CODE",
          labels: data.scanAnalog.scans.monthName,
          txtValues: data.scanAnalog.scans.arrowType.map((i) =>
            i == "UP" ? "GREEN" : "RED"
          ),
          visible: false,
        },
        {
          streamCode: "ANALOG_COLOR_CODE",
          labels: data.scanAnalog.analog.monthName,
          txtValues: data.scanAnalog.analog.arrowType.map((i) =>
            i == "UP" ? "GREEN" : "RED"
          ),
          visible: false,
        },
        {
          streamCode: "DIGITAL_DELTA_PCT",
          labels: data.scanAnalog.scans.monthName,
          values: data.scanAnalog.scans.trendPct,
          visible: false,
        },
        {
          streamCode: "ANALOG_DELTA_PCT",
          labels: data.scanAnalog.analog.monthName,
          values: data.scanAnalog.analog.trendPct,
          visible: false,
        },
      ];
    }


    if (data.itemRadar) {
      this.itemsRadar = [
        {
          streamCode: "LABELS",
          labels: data.itemRadar.m1.itemGroupDescr,
        },
        {
          streamCode: "M1",
          descr: data.itemRadar.m1.monthDescr,
          color: this.colorCodeToRGB("BLUE"),
          bgColor: this.colorCodeToRGB("BLUE", 0.2),
          labels: data.itemRadar.m1.itemGroupDescr,
          values: data.itemRadar.m1.valCnt,
          values2: data.itemRadar.m1.avgPrice,
          visible: true,
        },
        {
          streamCode: "M1",
          descr: data.itemRadar.m2.monthDescr,
          color: this.colorCodeToRGB("RED"),
          bgColor: this.colorCodeToRGB("RED", 0.2),
          labels: data.itemRadar.m2.itemGroupDescr,
          values: data.itemRadar.m2.valCnt,
          values2: data.itemRadar.m2.avgPrice,
          visible: true,
        },
        {
          streamCode: "M1",
          descr: data.itemRadar.m3.monthDescr,
          color: this.colorCodeToRGB("YELLOW"),
          bgColor: this.colorCodeToRGB("YELLOW", 0.2),
          labels: data.itemRadar.m3.itemGroupDescr,
          values: data.itemRadar.m3.valCnt,
          values2: data.itemRadar.m3.avgPrice,
          visible: true,
        },
      ];
    }

    if (data.tryInTrends) {
      this.tryInTrends = [
        {
          streamCode: "LABELS",
          labels: this.monthNames,
        },
      ];

      if (data.tryInTrends.tryIn) {
        this.tryInTrends.push(
          {
            streamCode: "TRY_IN",
            descr: this.curYear + " כלל הלקוחות %",
            color: this.colorCodeToRGB("YELLOW"),
            labels: data.tryInTrends.tryIn.monthName,
            values2: data.tryInTrends.tryIn.valCnt,
            values: data.tryInTrends.tryIn.valSharePct,
            visible: true,
          },
          {
            streamCode: "TRY_IN_PREV",
            descr: this.lastYear + " כלל הלקוחות %",
            fillColor: this.colorCodeToRGB("YELLOW", 0.2),
            color: this.colorCodeToRGB("YELLOW"),
            labels: data.tryInTrends.tryInPrev.monthName,
            values2: data.tryInTrends.tryInPrev.valCnt,
            values: data.tryInTrends.tryInPrev.valSharePct,
            visible: true,
            dashed: true,
          }
        );
      }

      if (data.tryInTrends.tryInClinic) {
        this.tryInTrends.push(
          ...[
            {
              streamCode: "TRY_IN_CLINIC",
              descr: this.curYear + " ללקוח %",
              color: this.colorCodeToRGB("BLUE"),
              labels: data.tryInTrends.tryInClinic.monthName,
              values2: data.tryInTrends.tryInClinic.valCnt,
              values: data.tryInTrends.tryInClinic.valSharePct,
              visible: true,
            },
            {
              streamCode: "TRY_IN_CLINIC_PREV",
              descr: this.lastYear + " ללקוח %",
              color: this.colorCodeToRGB("BLUE"),
              fillColor: this.colorCodeToRGB("BLUE", 0.2),
              labels: data.tryInTrends.tryInClinicPrev.monthName,
              values2: data.tryInTrends.tryInClinicPrev.valCnt,
              values: data.tryInTrends.tryInClinicPrev.valSharePct,
              visible: true,
              dashed: true,
            },
          ]
        );
      }
    }

    if (data.fixTrends) {
      this.fixTrends = [
        {
          streamCode: "LABELS",
          labels: this.monthNames,
        },
      ];

      if (data.fixTrends.fix) {
        this.fixTrends.push(
          {
            streamCode: "FIX",
            descr: this.curYear + " כלל הלקוחות %",
            color: this.colorCodeToRGB("YELLOW"),
            // fillColor: this.colorCodeToRGB("YELLOW", 0.2),
            labels: data.fixTrends.fix.monthName,
            values2: data.fixTrends.fix.valCnt,
            values: data.fixTrends.fix.valSharePct,
            visible: true,
          },
          {
            streamCode: "FIX_PREV",
            descr: this.lastYear + " כלל הלקוחות %",
            color: this.colorCodeToRGB("YELLOW"),
            fillColor: this.colorCodeToRGB("YELLOW", 0.2),
            labels: data.fixTrends.fixPrev.monthName,
            values2: data.fixTrends.fixPrev.valCnt,
            values: data.fixTrends.fixPrev.valSharePct,
            visible: true,
            dashed: true,
          }
        );
      }

      if (data.fixTrends.fixClinic) {
        this.fixTrends.push(
          ...[
            {
              streamCode: "FIX_CLINIC",
              descr: this.curYear + " ללקוח %",
              color: this.colorCodeToRGB("BLUE"),
              labels: data.fixTrends.fixClinic.monthName,
              values2: data.fixTrends.fixClinic.valCnt,
              values: data.fixTrends.fixClinic.valSharePct,
              visible: true,
            },
            {
              streamCode: "FIX_CLINIC_PREV",
              descr: this.lastYear + " ללקוח %",
              color: this.colorCodeToRGB("BLUE"),
              fillColor: this.colorCodeToRGB("BLUE", 0.2),
              labels: data.fixTrends.fixClinicPrev.monthName,
              values2: data.fixTrends.fixClinicPrev.valCnt,
              values: data.fixTrends.fixClinicPrev.valSharePct,
              visible: true,
              dashed: true,
            },
          ]
        );
      }
    }

    if (data.returnTrends) {
      this.returnTrends = [
        {
          streamCode: "LABELS",
          labels: this.monthNames,
        },
      ];

      if (data.returnTrends.return) {
        this.returnTrends.push(
          {
            streamCode: "RETURNS",
            descr: this.curYear + " כלל הלקוחות %",
            color: this.colorCodeToRGB("YELLOW"),
            labels: data.returnTrends.return.monthName,
            values2: data.returnTrends.return.valCnt,
            values: data.returnTrends.return.valSharePct,
            visible: true,
          },
          {
            streamCode: "RETURNS_PREV",
            descr: this.lastYear + " כלל הלקוחות %",
            color: this.colorCodeToRGB("YELLOW"),
            fillColor: this.colorCodeToRGB("YELLOW", 0.2),
            labels: data.returnTrends.returnPrev.monthName,
            values2: data.returnTrends.returnPrev.valCnt,
            values: data.returnTrends.returnPrev.valSharePct,
            visible: true,
            dashed: true,
          }
        );
      }

      if (data.returnTrends.returnClinic) {
        this.returnTrends.push(
          ...[
            {
              streamCode: "RETURNS_CLINIC",
              descr: this.curYear + " ללקוח %",
              color: this.colorCodeToRGB("BLUE"),
              labels: data.returnTrends.returnClinic.monthName,
              values2: data.returnTrends.returnClinic.valCnt,
              values: data.returnTrends.returnClinic.valSharePct,
              visible: true,
            },
            {
              streamCode: "RETURNS_CLINIC_PREV",
              descr: this.lastYear + " ללקוח %",
              color: this.colorCodeToRGB("BLUE"),
              fillColor: this.colorCodeToRGB("BLUE", 0.2),
              labels: data.returnTrends.returnClinicPrev.monthName,
              values2: data.returnTrends.returnClinicPrev.valCnt,
              values: data.returnTrends.returnClinicPrev.valSharePct,
              visible: true,
              dashed: true,
            },
          ]
        );
      }
    }

    if (data.slScore) {
      data.slColor = this.colorCodeToRGB(data.slColor.toUpperCase());
      data.slMetrics.forEach((m) => {
        if (m.color)
          m.displayColor = this.colorCodeToRGB(m.color.toUpperCase());
      });
    }

    if (data.employeePerformance && data.employeePerformance.length > 0) {
      this.employeePerformanceOptions = data.employeePerformance;
      this.selectedEmployeePerformance = data.employeePerformance[0];
    }

    this.cartesianTabsAvailable = [
    ];
    if (this.checkPermission('profit_trends')) {
      this.cartesianTabsAvailable.push('profit_trends');
      if (!this.cartesianTabSelected)
        this.cartesianTabSelected = 'profit_trends';
    }
    if (this.checkPermission('return_trends')) {
      this.cartesianTabsAvailable.push('return_trends');
      if (!this.cartesianTabSelected)
        this.cartesianTabSelected = 'return_trends';
    }
    if (this.checkPermission('fix_trends')) {
      this.cartesianTabsAvailable.push('fix_trends');
      if (!this.cartesianTabSelected)
        this.cartesianTabSelected = 'fix_trends';
    }
    if (this.checkPermission('try_in_trends')) {
      this.cartesianTabsAvailable.push('try_in_trends');
      if (!this.cartesianTabSelected)
        this.cartesianTabSelected = 'try_in_trends';
    }
    if (this.checkPermission('scan_analog')) {
      this.cartesianTabsAvailable.push('scan_analog');
      if (!this.cartesianTabSelected)
        this.cartesianTabSelected = 'scan_analog';
    }
    this.loading = false;
  }

  onCarouselChange(event: SlidesOutputData) {
    // console.log(event);
  }

  colorCodeToRGB(code: string, opacity: number = 1) {
    switch (code) {
      case "RED":
        return "rgba(255, 105, 105, " + opacity + ")";
      case "GREEN":
        return "rgba(50, 195, 125, " + opacity + ")";
      case "YELLOW":
        return "rgba(255, 187, 0, " + opacity + ")";
      case "BLUE":
        return "rgba(0, 148, 221, " + opacity + ")";
      case "DARK_RED":
        return "rgba(255, 75, 75, " + opacity + ")";
      case "DARK_GREEN":
        return "rgba(50, 165, 95, " + opacity + ")";
      case "DARK_YELLOW":
        return "rgba(255, 152, 0, " + opacity + ")";
      case "PURPLE":
        return "rgba(128, 0, 128, " + opacity + ")";
      default:
        return "rgba(0, 148, 221, " + opacity + ")";
    }
  }

  stoplightClick(color: string, cnt: number) {
    if (cnt == 0) return;
    this.abandonClinicsSw = false;
    this.failedAcuisitionSw = false;

    switch (color) {
      case "GREEN":
        this.clinicListTitle = "לקוחות ירוקים";
        break;
      case "RED":
        this.clinicListTitle = "לקוחות אדומים";
        break;
      case "YELLOW":
        this.clinicListTitle = "לקוחות צהובים";
        break;
      case "ABANDON":
        this.clinicListTitle = "לקוחות נוטשים";
        this.abandonClinicsSw = true;
        break;
      case "ACUISITION_FAIL":
        this.clinicListTitle = "גיוסים כושלים";
        this.failedAcuisitionSw = true;
        break;
    }

    this.clinicsStoplightLoading = true;
    this.displayClinicList = true;

    let agentCode;
    if (this.isAgent) {
      agentCode = this.authService.currentUser.agentCode;
    } else if (this.filterType == "AGENT") {
      agentCode = this.filter;
    }

    this.reportsService.getCliStoplightDrill(color, agentCode, this.filter, this.filterType).subscribe(
      (data) => {
        this.clinics = data;
        this.clinics.forEach((c) => {
          c.metrics.forEach((m) => {
            m.displayColor = this.colorCodeToRGB(m.color.toUpperCase());
          });
        });
        this.clinicsStoplightLoading = false;
      },
      (err) => {
        this.displayClinicList = false;
      }
    );
  }

  goToClinicKpi(clinicId: number) {
    this.store.dispatch(
      GO_TO_CLINIC_KPI({
        clinicId: clinicId,
      })
    );
  }

  activateSnooze(clinicId: number) {
    this.broshService
      .updateClinicSnooze(clinicId, true, this.snoozeNote, this.snoozeEndDate)
      .subscribe(() => {
        this.snoozeNote = null;
        this.snoozeEndDate = null;
        this.getData();
        this.stoplightClick("ABANDON", 1);
        this.toggleSnoozeInput = false;
      });
  }

  toggleSnoozeArea(event: MouseEvent) {
    event.stopPropagation();
    this.toggleSnoozeInput = !this.toggleSnoozeInput;
  }

  checkPermission(featureCode: string): boolean {

    return this.store.selectSignal(kpiConfig)().find((c) => c.featureCode == featureCode && c.filterType == this.filterType) != null;
  }


}